import i18next, {Namespace, ParseKeys, TOptions} from 'i18next'
import Backend from 'i18next-http-backend'
import LanguageDetector from 'i18next-browser-languagedetector'
import {initReactI18next, TransProps, useTranslation as i18nUseTranslation, Trans as I18nTrans} from 'react-i18next'
//translations
import de from './de/de.json'
import en from './en/en.json'
import es from './es/es.json'
import fr from './fr/fr.json'
import it from './it/it.json'
import ja from './ja/ja.json'
import ko from './ko/ko.json'
import nl from './nl/nl.json'
import pt from './pt/pt.json'
import ru from './ru/ru.json'
import zh from './zh/zh.json'
import es_LATAM from './es_LATAM/es_LATAM.json'
import es_MX from './es_MX/es_MX.json'
import fr_CA from './fr_CA/fr_CA.json'
import pt_br from './pt_br/pt_br.json'
import en_CA from './en_CA/en_CA.json'
import {_DefaultNamespace} from 'react-i18next/TransWithoutContext'
import {HTMLProps} from 'react'

export type AppLanguage = {
    label: string
    code: string
    flagCode: string
    json: object
}

export const DEFAULT_LANGUAGE = {
    label: 'English (EN)',
    code: 'en',
    flagCode: 'gb',
    json: en
} as const satisfies AppLanguage

export const APP_LANGUAGES = [
    DEFAULT_LANGUAGE,
    {label: 'English (EN-CA)', code: 'en_CA', flagCode: 'ca', json: en_CA},
    {label: 'Español (ES)', code: 'es', flagCode: 'es', json: es},
    {label: 'Español (ES-LATAM)', code: 'es_419', flagCode: 'mx', json: es_LATAM},
    {label: 'Español (ES-MX)', code: 'es_MX', flagCode: 'mx', json: es_MX},
    {label: 'Deutsch (DE)', code: 'de', flagCode: 'de', json: de},
    {label: 'Français (FR)', code: 'fr', flagCode: 'fr', json: fr},
    {label: 'Français (FR-CA)', code: 'fr_CA', flagCode: 'ca', json: fr_CA},
    {label: 'Italiano (IT)', code: 'it', flagCode: 'it', json: it},
    {label: '日本語 (JP)', code: 'ja', flagCode: 'jp', json: ja},
    {label: '한국어 (KO)', code: 'ko', flagCode: 'kr', json: ko},
    {label: 'Nederlands (NL)', code: 'nl', flagCode: 'nl', json: nl},
    {label: 'Português (PT)', code: 'pt', flagCode: 'pt', json: pt},
    {label: 'Português (PT-BR)', code: 'pt_BR', flagCode: 'br', json: pt_br},
    {label: 'Русский (RU)', code: 'ru', flagCode: 'ru', json: ru},
    {label: '中文 (ZH)', code: 'zh', flagCode: 'cn', json: zh}
] as const satisfies readonly AppLanguage[]

/** Union of the actual language codes, so consumers can be checked for exhaustiveness. */
export type AppLanguageCode = (typeof APP_LANGUAGES)[number]['code']

i18next
    .use(Backend)
    .use(LanguageDetector)
    .use(initReactI18next)
    .init({
        resources: Object.fromEntries(APP_LANGUAGES.map(language => [language.code, language.json])),
        ns: [],
        defaultNS: '',
        keySeparator: false,
        fallbackLng: APP_LANGUAGES[0].code,
        debug: false,
        react: {
            useSuspense: false
        },
        supportedLngs: APP_LANGUAGES.map(language => language.code),
        interpolation: {
            escapeValue: false
        },
        detection: {
            order: ['localStorage', 'navigator'],
            lookupLocalStorage: 'i18nextLng'
        }
    })

export const changeAppLanguage = async (nextLanguage: (typeof APP_LANGUAGES)[number]['code']) => {
    await i18next.changeLanguage(nextLanguage)
}

/*These types are needed to get the TKey list*/
type RemoveSuffix<S extends string> = S extends `${infer P}_other` | `${infer P}_one` ? P : S
type FlattenObjectKeys<T, Prefix extends string = ''> = {
    [K in keyof T & string]: T[K] extends object
        ? FlattenObjectKeys<T[K], `${Prefix}${K}:`>
        : RemoveSuffix<`${Prefix}${K}`>
}[keyof T & string]

export type TKey = RemoveSuffix<FlattenObjectKeys<typeof en>>

export const useTranslation = () => {
    const translation = i18nUseTranslation()

    return {
        ...translation,
        t: (key: TKey, options?: TOptions) => translation.t(key, options)
    }
}

export const Trans = <
    Key extends ParseKeys<Ns, TOpt, KPrefix>,
    Ns extends Namespace = _DefaultNamespace,
    TOpt extends TOptions = Record<string, unknown>,
    KPrefix = undefined,
    E = HTMLProps<HTMLDivElement>,
    I18NKey extends TKey | undefined = undefined
>({
    i18nKey,
    ...props
}: Omit<TransProps<Key, Ns, TOpt, KPrefix, E>, 'i18nKey'> & {i18nKey: I18NKey}) => (
    <I18nTrans {...props} i18nKey={i18nKey} />
)

const i18n = {...i18next, t: (key: TKey, options?: TOptions) => i18next.t(key, options)}

export default i18n
